import { useState, useMemo, useEffect, useCallback } from "react";
import { useAllProjectsFlat } from "../hooks/useFirebase";

function phClass(index) {
  return ["", "dark", "blue"][index % 3];
}

function catFromTypeName(name = "") {
  const n = name.toLowerCase();
  if (n.includes("aluminium") || n.includes("aluminum")) return "aluminium";
  if (n.includes("cladding")) return "cladding";
  if (n.includes("partition")) return "partitions";
  if (n.includes("glass")) return "glass";
  return "aluminium";
}

const FILTERS = [
  { id: "all",        label: "All" },
  { id: "aluminium",  label: "Aluminium" },
  { id: "cladding",   label: "Cladding" },
  { id: "partitions", label: "Partitions" },
  { id: "glass",      label: "Glass" },
];

function Lightbox({ project, onClose }) {
  const photos = project.photos || [];
  const [idx, setIdx] = useState(0);

  const prev = useCallback(() => setIdx(i => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % photos.length), [photos.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft")  prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, prev, next]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,10,10,0.92)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: "min(960px, 95vw)", background: "#fff", border: "1px solid var(--ink)", display: "flex", flexDirection: "column", maxHeight: "92vh" }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: "1px solid var(--ink)" }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", border: "1px solid var(--line)", padding: "3px 8px" }}>
              {project.typeName}
            </span>
            {photos.length > 0 && (
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.1em", color: "var(--muted)" }}>
                {idx + 1} / {photos.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", border: "1px solid var(--line)", padding: "6px 12px", background: "#fff", cursor: "pointer" }}
          >
            Close ×
          </button>
        </div>

        {/* Image */}
        <div style={{ position: "relative", background: "#f0f0f0", flexShrink: 0 }}>
          {photos.length > 0 ? (
            <img
              key={idx}
              src={photos[idx].url}
              alt={project.name}
              style={{ width: "100%", maxHeight: "56vh", objectFit: "contain", display: "block" }}
            />
          ) : (
            <div className={`ph ${phClass(0)}`} style={{ height: "340px" }}>
              <div className="ph-label">{project.typeName}</div>
            </div>
          )}

          {/* Prev / Next */}
          {photos.length > 1 && (
            <>
              <button
                onClick={prev}
                style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "56px", background: "rgba(10,10,10,0.35)", border: "none", cursor: "pointer", color: "#fff", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                ←
              </button>
              <button
                onClick={next}
                style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "56px", background: "rgba(10,10,10,0.35)", border: "none", cursor: "pointer", color: "#fff", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                →
              </button>
            </>
          )}
        </div>

        {/* Thumbnail strip */}
        {photos.length > 1 && (
          <div style={{ display: "flex", gap: "4px", padding: "8px 12px", borderTop: "1px solid var(--line)", overflowX: "auto", background: "#fafafa" }}>
            {photos.map((ph, i) => (
              <img
                key={i}
                src={ph.url}
                alt=""
                onClick={() => setIdx(i)}
                style={{ width: "64px", height: "48px", objectFit: "cover", cursor: "pointer", flexShrink: 0, border: i === idx ? "2px solid var(--blue)" : "2px solid transparent", opacity: i === idx ? 1 : 0.55 }}
              />
            ))}
          </div>
        )}

        {/* Info */}
        <div style={{ padding: "20px", borderTop: "1px solid var(--line)" }}>
          <h3 style={{ fontSize: "22px", fontWeight: 500, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{project.name}</h3>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>
            <span>{project.desc || ""}</span>
            <span>{project.year || ""}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Projects({ types, loading }) {
  const [active, setActive] = useState("all");
  const [selected, setSelected] = useState(null);
  const projects = useAllProjectsFlat(types);

  const enriched = useMemo(() =>
    projects.map(p => ({ ...p, cat: catFromTypeName(p.typeName) })),
    [projects]
  );

  const counts = useMemo(() => {
    const c = { all: enriched.length, aluminium: 0, cladding: 0, partitions: 0, glass: 0 };
    enriched.forEach(p => { if (c[p.cat] !== undefined) c[p.cat]++; });
    return c;
  }, [enriched]);

  const visible = active === "all" ? enriched : enriched.filter(p => p.cat === active);

  return (
    <section id="work" style={{ padding: "120px 0", borderBottom: "1px solid var(--ink)", background: "#fff" }}>
      <div style={{ maxWidth: "1440px", margin: "0 auto", padding: "0 32px" }}>

        {/* Header */}
        <div className="projects-head" style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "end", gap: "40px", marginBottom: "48px" }}>
          <div>
            <div className="section-tag">// Selected work — No. 04</div>
            <h2 style={{ fontSize: "clamp(40px, 4.5vw, 64px)", lineHeight: 0.98, letterSpacing: "-0.025em", fontWeight: 500, marginTop: "12px" }}>
              Recent{" "}
              <span style={{ fontWeight: 400, color: "var(--blue)" }}>projects</span>{" "}
              across<br />Sri Lanka.
            </h2>
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {FILTERS.map(f => (
              <button
                key={f.id}
                className={`filter-btn${active === f.id ? " active" : ""}`}
                onClick={() => setActive(f.id)}
              >
                {f.label}
                <span className="fcount" style={{ marginLeft: "8px", color: "var(--muted)" }}>
                  {counts[f.id]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div style={{ borderTop: "1px solid var(--ink)", padding: "60px 0", fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)" }}>
            // Loading projects…
          </div>
        ) : visible.length === 0 ? (
          <div style={{ borderTop: "1px solid var(--ink)", borderLeft: "1px solid var(--ink)", padding: "60px 32px", display: "flex", alignItems: "center", borderRight: "1px solid var(--ink)", borderBottom: "1px solid var(--ink)" }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)" }}>
              // No projects yet — add some via the admin panel.
            </span>
          </div>
        ) : (
          <div
            className="project-grid"
            style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, borderTop: "1px solid var(--ink)", borderLeft: "1px solid var(--ink)" }}
          >
            {visible.map((p, i) => (
              <article
                key={p.id}
                className="project-card"
                onClick={() => setSelected(p)}
                style={{ borderRight: "1px solid var(--ink)", borderBottom: "1px solid var(--ink)", padding: "14px", display: "flex", flexDirection: "column", gap: "14px", background: "#fff", cursor: "pointer" }}
              >
                <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: "10px", left: "10px", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.08em", background: "rgba(255,255,255,0.9)", padding: "4px 8px", color: "var(--ink)", zIndex: 2 }}>
                    PRJ · {String(i + 1).padStart(3, "0")}
                  </div>
                  {p.photos && p.photos.length > 0 ? (
                    <img
                      src={p.photos[0].url}
                      alt={p.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.4s" }}
                    />
                  ) : (
                    <div className={`ph ${phClass(i)}`} style={{ height: "100%" }}>
                      <div className="ph-label">{p.typeName} · {p.year || ""}</div>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  <span className="project-tag" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 8px", border: "1px solid var(--line)", color: "var(--muted)" }}>
                    {p.typeName}
                  </span>
                </div>
                <h4 style={{ fontSize: "18px", fontWeight: 500, letterSpacing: "-0.01em", lineHeight: 1.2 }}>{p.name}</h4>
                <div className="project-meta" style={{ display: "flex", justifyContent: "space-between", fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.08em", color: "var(--muted)", marginTop: "4px", textTransform: "uppercase" }}>
                  <span>{p.desc || "—"}</span>
                  <span>{p.year || "—"}</span>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA */}
        <div style={{ paddingTop: "48px", textAlign: "center" }}>
          <a href="#contact" className="btn btn-ghost">Discuss your project →</a>
        </div>
      </div>

      {selected && <Lightbox project={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
